import { Badge } from "@aitisal/ui/retail-storefront/badge";
import { Button } from "@aitisal/ui/retail-storefront/button";
import { Progress } from "@aitisal/ui/retail-storefront/progress";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Package } from "lucide-react";

import { OrderStatus, SalesChannelType } from "@aitisal/contracts/common";
import { getActiveOrders } from "@aitisal/sdk/restaurant/order";
import { useOrders } from "../../stores/ordersStore";
import { useSession } from "../../stores/sessionStore";

const getStatusLabel = (status?: OrderStatus) => {
  switch (status) {
    case OrderStatus.NEW:
      return "Order Received";
    case OrderStatus.ACCEPTED:
      return "Preparing your order";
    case OrderStatus.CLOSED:
      return "Order ready!";
    case OrderStatus.CANCELLED:
      return "Order cancelled";
    default:
      return "Order ready!";
  }
};

const getStatusBadgeClass = (status?: OrderStatus) => {
  switch (status) {
    case OrderStatus.NEW:
      return "bg-blue-50 text-blue-700 border-blue-200";
    case OrderStatus.ACCEPTED:
      return "bg-yellow-50 text-yellow-700 border-yellow-200";
    case OrderStatus.CLOSED:
      return "bg-green-50 text-green-700 border-green-200";
    case OrderStatus.CANCELLED:
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

const getProgressValue = (status?: OrderStatus) => {
  if (status === OrderStatus.NEW) return 33;
  if (status === OrderStatus.ACCEPTED) return 66;
  return 100;
};

export default function ActiveOrders() {
  const navigate = useNavigate();
  const { restaurantId } = useSession();
  const { orderIds } = useOrders();

  const allOrderIds = useMemo(() => {
    const storedOrderIds: string[] = JSON.parse(
      localStorage.getItem("placedOrders") || "[]",
    );
    return Array.from(new Set([...orderIds, ...storedOrderIds]));
  }, [orderIds]);

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ["active-orders-banner", restaurantId, allOrderIds.join(",")],
    queryFn: async () => {
      if (!restaurantId || allOrderIds.length === 0) return { items: [] };
      const response = await getActiveOrders(
        restaurantId,
        SalesChannelType.WEB,
        allOrderIds,
      );
      return response.data;
    },
    enabled: Boolean(restaurantId) && allOrderIds.length > 0,
    refetchInterval: 30000,
  });

  const activeOrders = ordersData?.items || [];
  if (isLoading || activeOrders.length === 0) return null;

  const order = activeOrders[0];
  const showProgress = activeOrders.length === 1;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-5">
      <Button
        variant="ghost"
        onClick={() => navigate({ to: "/c/ordertracking" })}
        className="group w-full rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden text-left transition-shadow hover:shadow-md hover:bg-white p-0 h-auto justify-start"
      >
        <div className="w-full px-4 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="relative h-11 w-11 shrink-0 rounded-full flex items-center justify-center"
              style={{
                background: "color-mix(in srgb, var(--brand-color) 12%, transparent)",
                color: "var(--brand-color)",
              }}
            >
              <Package className="h-5 w-5" />
              <span className="absolute top-1 right-1 inline-flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-gray-900 truncate">
                  {activeOrders.length > 1
                    ? `${activeOrders.length} Active Orders`
                    : getStatusLabel(order.status)}
                </span>
                {activeOrders.length === 1 && (
                  <Badge
                    variant="outline"
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${getStatusBadgeClass(order.status)}`}
                  >
                    {order.status}
                  </Badge>
                )}
              </div>
              <span className="text-sm text-gray-500 truncate">
                {activeOrders.length > 1
                  ? "View and track all your orders"
                  : `Order #${order.orderNumber || order.orderId?.slice(0, 8).toUpperCase()}`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0" style={{ color: "var(--brand-color)" }}>
            <span className="hidden sm:inline text-sm font-semibold">Track</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>

        {showProgress && (
          <div className="px-4 pb-3">
            <Progress
              value={getProgressValue(order.status)}
              className="h-1 bg-gray-100 [&>div]:bg-primary"
            />
          </div>
        )}
      </Button>
    </div>
  );
}